import type {
  ActivateAssistsParams,
  ActivateAssistsPayload,
  AutoScoreStudentWorkbookParams,
  AutoScoreStudentWorkbookPayload,
  CommonResponseSliceGroupWorkbookPageToStudentWorkbookProgressResponse,
  CommonResponseSliceGroupWorkbookProblemToStudentWorkbookScoringAndStudentResponse,
  CommonResponseSliceGroupWorkbookToRoundToStudentWorkbookResponse,
  CommonResponseSliceWorkbookAndStudentWorkbookResponse,
  CommonResponseSliceWorkbookProblemToStudentWorkbookScoringResponse,
  CommonResponseWorkbookAndRoundAndStudentWorkbookProgressWithWorkbookPageResponse,
  DeleteVersionPayload,
  GetGradeWorkbookProgressParams,
  GetGradeWorkbookScoringParams,
  GetLessonClassProgressParams,
  GetLessonClassWorkbookScoringParams,
  GetStudentWorkbookProgressParams,
  GetStudentWorkbooksByGradeParams,
  GetStudentWorkbooksByLessonClassIdParams,
  GetStudentWorkbookScoringParams,
  GetStudentWorkbooksParams,
  Pageable,
  RemoveStudentWorkbookPayload,
  ScoringWorkbookProblemPayload,
  SetProgressOfPageData,
  StudentWorkbookProgressRequest,
  StudentWorkbookRequest,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'
import type { Slice } from '~/schema/slice'

import {
  adaptGroupStudentWorkbooks,
  adaptGroupWorkbookPageProgresses,
  adaptGroupWorkbookScoredProblems,
  adaptStudentWorkbookList,
  adaptStudentWorkbookListDetail,
  adaptStudentWorkbookProgressList,
  adaptStudentWorkbookProgressStatus,
  adaptStudentWorkbookScoredProblemDetails,
  adaptStudentWorkbookScoredProblems,
} from './adapters'
import type {
  GroupStudentWorkbook,
  GroupWorkbookPageProgress,
  GroupWorkbookScoredProblem,
  StudentWorkbookListItem,
  StudentWorkbookListItemDetail,
  StudentWorkbookProgressList,
  StudentWorkbookScoredProblem,
  StudentWorkbookScoredProblemDetail,
} from './schemas'

export type StudentWorkbookAssistParams = ActivateAssistsParams
export type StudentWorkbookAssistPayload = ActivateAssistsPayload
export type StudentWorkbookAutoScoringParams = AutoScoreStudentWorkbookParams
export type StudentWorkbookAutoScoringPayload = AutoScoreStudentWorkbookPayload
export type StudentWorkbookScoringPayload = ScoringWorkbookProblemPayload

// 생성 타입은 Spring Pageable 을 `pageable` 객체로 그리지만 실제 쿼리는 page·size·sort 를 평탄하게 받는다.
export type StudentWorkbookListParams = Omit<GetStudentWorkbooksParams, 'pageable' | 'studentId'> &
  Pageable
export type StudentWorkbookProgressListTarget = Omit<GetStudentWorkbookProgressParams, 'pageable'>
export type StudentWorkbookScoredProblemsTarget = Omit<GetStudentWorkbookScoringParams, 'pageable'>

/** 문풀동 시청 기록 — 채점 전/후 어느 시점에 봤는지를 남긴다 */
export const activateWorkbookAssists = async (
  { studentWorkbookProgressId, workbookProblemId }: StudentWorkbookAssistParams,
  payload: StudentWorkbookAssistPayload,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<unknown>(
    `/student-workbook/${studentWorkbookProgressId}/${workbookProblemId}/assist`,
    payload,
    { meta },
  )
}

export const autoScoreStudentWorkbook = async (
  params: StudentWorkbookAutoScoringParams,
  payload: StudentWorkbookAutoScoringPayload,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<unknown>('/student-workbook/auto-scoring', payload, {
    params,
    meta,
  })
}

export const scoreStudentWorkbook = async (
  payload: StudentWorkbookScoringPayload,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<unknown>('/student-workbook/scoring', payload, { meta })
}

/***************** 학생 교재 조회 *****************/

export const getStudentWorkbooks = async (
  studentId: GetStudentWorkbooksParams['studentId'],
  params: StudentWorkbookListParams,
  meta?: RequestMeta,
): Promise<Slice<StudentWorkbookListItem>> => {
  const body = await httpClient().get<CommonResponseSliceWorkbookAndStudentWorkbookResponse>(
    `/student-workbook/student/${studentId}`,
    { params, meta },
  )
  return adaptStudentWorkbookList(body.data)
}

export const getStudentWorkbookProgressList = async (
  { studentId, studentWorkbookId, revisionId }: StudentWorkbookProgressListTarget,
  params?: Pageable,
  meta?: RequestMeta,
): Promise<StudentWorkbookProgressList> => {
  const body =
    await httpClient().get<CommonResponseWorkbookAndRoundAndStudentWorkbookProgressWithWorkbookPageResponse>(
      `/student-workbook/student/${studentId}/${studentWorkbookId}/${revisionId}`,
      { params, meta },
    )
  return adaptStudentWorkbookProgressList(body.data)
}

export const getStudentWorkbookScoredProblems = async (
  { studentId, studentWorkbookId, revisionId, progressId }: StudentWorkbookScoredProblemsTarget,
  params?: Pageable,
  meta?: RequestMeta,
): Promise<Slice<StudentWorkbookScoredProblem>> => {
  const body =
    await httpClient().get<CommonResponseSliceWorkbookProblemToStudentWorkbookScoringResponse>(
      `/student-workbook/student/${studentId}/${studentWorkbookId}/${revisionId}/${progressId}`,
      { params, meta },
    )
  return adaptStudentWorkbookScoredProblems(body.data)
}

/***************** 선생님 앱의 학생 교재 조회 *****************/
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 studentWorkbook/controller/StudentWorkbookController.kt 로 확인.

export const getStudentWorkbookDetails = async (
  studentId: GetStudentWorkbooksParams['studentId'],
  params: StudentWorkbookListParams,
  meta?: RequestMeta,
): Promise<Slice<StudentWorkbookListItemDetail>> => {
  const body = await httpClient().get<CommonResponseSliceWorkbookAndStudentWorkbookResponse>(
    `/student-workbook/student/${studentId}`,
    { params, meta },
  )
  return adaptStudentWorkbookListDetail(body.data)
}

export const getStudentWorkbookScoredProblemDetails = async (
  { studentId, studentWorkbookId, revisionId, progressId }: StudentWorkbookScoredProblemsTarget,
  params?: Pageable,
  meta?: RequestMeta,
): Promise<Slice<StudentWorkbookScoredProblemDetail>> => {
  const body =
    await httpClient().get<CommonResponseSliceWorkbookProblemToStudentWorkbookScoringResponse>(
      `/student-workbook/student/${studentId}/${studentWorkbookId}/${revisionId}/${progressId}`,
      { params, meta },
    )
  return adaptStudentWorkbookScoredProblemDetails(body.data)
}

/***************** 학년·반 교재 *****************/

export type GroupStudentWorkbookListParams = Omit<
  GetStudentWorkbooksByGradeParams,
  'pageable' | 'schoolType' | 'grade'
> &
  Pageable
export type GradeStudentWorkbookTarget = Pick<
  GetStudentWorkbooksByGradeParams,
  'schoolType' | 'grade'
>
export type LessonClassStudentWorkbookTarget = Pick<
  GetStudentWorkbooksByLessonClassIdParams,
  'lessonClassId'
>
export type GradeWorkbookProgressTarget = Omit<GetGradeWorkbookProgressParams, 'pageable'>
export type LessonClassWorkbookProgressTarget = Omit<GetLessonClassProgressParams, 'pageable'>
export type GradeWorkbookScoringTarget = Omit<GetGradeWorkbookScoringParams, 'pageable'>
export type LessonClassWorkbookScoringTarget = Omit<GetLessonClassWorkbookScoringParams, 'pageable'>
export type GroupWorkbookPageParams = Pageable

export const getGradeStudentWorkbooks = async (
  { schoolType, grade }: GradeStudentWorkbookTarget,
  params: GroupStudentWorkbookListParams,
  meta?: RequestMeta,
): Promise<Slice<GroupStudentWorkbook>> => {
  const body =
    await httpClient().get<CommonResponseSliceGroupWorkbookToRoundToStudentWorkbookResponse>(
      `/student-workbook/grade/${schoolType}/${grade}`,
      { params, meta },
    )
  return adaptGroupStudentWorkbooks(body.data)
}

export const getLessonClassStudentWorkbooks = async (
  { lessonClassId }: LessonClassStudentWorkbookTarget,
  params: GroupStudentWorkbookListParams,
  meta?: RequestMeta,
): Promise<Slice<GroupStudentWorkbook>> => {
  const body =
    await httpClient().get<CommonResponseSliceGroupWorkbookToRoundToStudentWorkbookResponse>(
      `/student-workbook/lesson-classes/${lessonClassId}`,
      { params, meta },
    )
  return adaptGroupStudentWorkbooks(body.data)
}

/** 학년 교재 한 회차의 페이지별 진도. 정렬을 주지 않으면 서버가 페이지 순으로 낸다 */
export const getGradeWorkbookPageProgresses = async (
  { schoolType, grade, workbookId, round }: GradeWorkbookProgressTarget,
  params?: GroupWorkbookPageParams,
  meta?: RequestMeta,
): Promise<Slice<GroupWorkbookPageProgress>> => {
  const body =
    await httpClient().get<CommonResponseSliceGroupWorkbookPageToStudentWorkbookProgressResponse>(
      `/student-workbook/grade/${schoolType}/${grade}/${workbookId}/${round}`,
      { params, meta },
    )
  return adaptGroupWorkbookPageProgresses(body.data)
}

export const getLessonClassWorkbookPageProgresses = async (
  { lessonClassId, workbookId, round }: LessonClassWorkbookProgressTarget,
  params?: GroupWorkbookPageParams,
  meta?: RequestMeta,
): Promise<Slice<GroupWorkbookPageProgress>> => {
  const body =
    await httpClient().get<CommonResponseSliceGroupWorkbookPageToStudentWorkbookProgressResponse>(
      `/student-workbook/lesson-classes/${lessonClassId}/${workbookId}/${round}`,
      { params, meta },
    )
  return adaptGroupWorkbookPageProgresses(body.data)
}

export const getGradeWorkbookScoredProblems = async (
  { schoolType, grade, workbookId, round, pageId }: GradeWorkbookScoringTarget,
  params?: GroupWorkbookPageParams,
  meta?: RequestMeta,
): Promise<Slice<GroupWorkbookScoredProblem>> => {
  const body =
    await httpClient().get<CommonResponseSliceGroupWorkbookProblemToStudentWorkbookScoringAndStudentResponse>(
      `/student-workbook/grade/${schoolType}/${grade}/${workbookId}/${round}/${pageId}`,
      { params, meta },
    )
  return adaptGroupWorkbookScoredProblems(body.data)
}

export const getLessonClassWorkbookScoredProblems = async (
  { lessonClassId, workbookId, round, pageId }: LessonClassWorkbookScoringTarget,
  params?: GroupWorkbookPageParams,
  meta?: RequestMeta,
): Promise<Slice<GroupWorkbookScoredProblem>> => {
  const body =
    await httpClient().get<CommonResponseSliceGroupWorkbookProblemToStudentWorkbookScoringAndStudentResponse>(
      `/student-workbook/lesson-classes/${lessonClassId}/${workbookId}/${round}/${pageId}`,
      { params, meta },
    )
  return adaptGroupWorkbookScoredProblems(body.data)
}

/***************** 출제와 진도 *****************/

// 서버는 같은 값을 옛 이름(workbookIdList·studentIdList)으로도 받는다. 앱은 새 이름만 보낸다
export type StudentWorkbookAssignBody = Required<
  Pick<StudentWorkbookRequest, 'workbookIds' | 'studentIds'>
>

/** 학생들에게 교재를 출제한다. 이미 받은 학생은 회차가 하나 는다 */
export const assignStudentWorkbooks = async (
  body: StudentWorkbookAssignBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<unknown>('/student-workbook', body, { meta })
}

/** 출제한 학생 교재를 지운다 */
export const deleteStudentWorkbooks = async (
  studentWorkbookIds: RemoveStudentWorkbookPayload,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<unknown>('/student-workbook', { data: studentWorkbookIds, meta })
}

/** 학생 교재의 회차를 지운다 */
export const deleteStudentWorkbookRevisions = async (
  revisionIds: DeleteVersionPayload,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<unknown>('/student-workbook/version', { data: revisionIds, meta })
}

// 서버는 PROGRESS 로 바꾸는 요청을 거절한다. 미진행·완료만 보낸다
export type StudentWorkbookProgressStatusBody = Omit<StudentWorkbookProgressRequest, 'status'> & {
  status: Exclude<StudentWorkbookProgressRequest['status'], 'PROGRESS'>
}

/** 진도 여러 개의 상태를 바꾸고, 바꾼 뒤 그 페이지들을 모은 상태를 받는다 */
export const updateStudentWorkbookProgressStatus = async (
  body: StudentWorkbookProgressStatusBody,
  meta?: RequestMeta,
): Promise<GroupWorkbookPageProgress['status']> => {
  const response = await httpClient().post<SetProgressOfPageData>(
    '/student-workbook/progress',
    body,
    {
      meta,
    },
  )
  return adaptStudentWorkbookProgressStatus(response.data)
}
